import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Navigation from './components/Navigation';
import Home from './components/Home';
import Menu from './components/Menu';
import SpitBraaiPage from './components/SpitBraaiPage';
import Decor from './components/Decor';
import EstimateCalculator from './components/EstimateCalculator';
import TeamBuilding from './components/TeamBuilding';
import Contact from './components/Contact';
import SoundHire from './components/SoundHire';
import VenueSupport from './components/VenueSupport';
import Footer from './components/Footer';
import WhatsAppButton from './components/WhatsAppButton';
import LocalSeoBanner from './components/LocalSeoBanner';
import ServiceLandingPage from './components/ServiceLandingPage';
import BlogPage from './components/BlogPage';

const slugToPage = {
  '/': 'home',
  '/menu': 'menu',
  '/catering': 'menu',
  '/spit-braai': 'spitbraai',
  '/decor': 'decor',
  '/sound-hire': 'sound',
  '/sound': 'sound',
  '/team-building': 'teambuilding',
  '/venue-support': 'venuesupport',
  '/tent-chair-table-hire': 'venuesupport',
  '/estimate': 'estimate',
  '/contact': 'contact',
  '/blog': 'blog',
  '/catering-services-gauteng': 'catering-services-gauteng',
  '/spit-braai-hire-gauteng': 'spit-braai-hire-gauteng',
  '/event-decor-gauteng': 'event-decor-gauteng',
  '/sound-hire-gauteng': 'sound-hire-gauteng',
  '/tent-chair-table-hire-gauteng': 'tent-chair-table-hire-gauteng',
  '/team-building-gauteng': 'team-building-gauteng',
  '/venue-booking-gauteng': 'venue-booking-gauteng',
};

const pageToSlug = {
  home: '/',
  menu: '/catering',
  spitbraai: '/spit-braai',
  decor: '/decor',
  sound: '/sound',
  soundhire: '/sound',
  teambuilding: '/team-building',
  venuesupport: '/venue-support',
  estimate: '/estimate',
  contact: '/contact',
  blog: '/blog',
  'catering-services-gauteng': '/catering-services-gauteng',
  'spit-braai-hire-gauteng': '/spit-braai-hire-gauteng',
  'event-decor-gauteng': '/event-decor-gauteng',
  'sound-hire-gauteng': '/sound-hire-gauteng',
  'tent-chair-table-hire-gauteng': '/tent-chair-table-hire-gauteng',
  'team-building-gauteng': '/team-building-gauteng',
  'venue-booking-gauteng': '/venue-booking-gauteng',
};

const getPageFromPathname = (pathname) => {
  if (slugToPage[pathname]) return slugToPage[pathname];
  if (pathname.startsWith('/blog/')) return 'blog';
  return 'home';
};

function App() {
  const [currentPage, setCurrentPage] = useState(getPageFromPathname(window.location.pathname));

  // Listen for navigation events from child components
  useEffect(() => {
    const handleNavigate = (event) => {
      setCurrentPage(event.detail);
    };
    const handlePopState = () => {
      setCurrentPage(getPageFromPathname(window.location.pathname));
    };

    window.addEventListener('navigate', handleNavigate);
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('navigate', handleNavigate);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    const nextSlug = pageToSlug[currentPage];
    if (!nextSlug) return;
    const pathname = window.location.pathname;
    if (currentPage === 'blog' && pathname.startsWith('/blog/')) return;
    if (nextSlug !== pathname) {
      window.history.pushState({}, '', nextSlug);
    }
  }, [currentPage]);

  const renderPage = () => {
    switch (currentPage) {
      case 'home':
        return <Home />;
      case 'menu':
        return <Menu />;
      case 'spitbraai':
        return <SpitBraaiPage />;
      case 'decor':
        return <Decor />;
      case 'sound':
      case 'soundhire':
        return <SoundHire />;
      case 'venuesupport':
        return <VenueSupport />;
      case 'estimate':
        return <EstimateCalculator />;
      case 'teambuilding':
        return <TeamBuilding />;
      case 'contact':
        return <Contact />;
      case 'blog':
        return <BlogPage />;
      case 'catering-services-gauteng':
      case 'spit-braai-hire-gauteng':
      case 'event-decor-gauteng':
      case 'sound-hire-gauteng':
      case 'tent-chair-table-hire-gauteng':
      case 'team-building-gauteng':
      case 'venue-booking-gauteng':
        return <ServiceLandingPage slug={currentPage} />;
      default:
        return <Home />;
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      <Navigation currentPage={currentPage} setCurrentPage={setCurrentPage} />
      <LocalSeoBanner />
      <AnimatePresence mode="wait">
        <motion.div
          key={currentPage}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          {renderPage()}
        </motion.div>
      </AnimatePresence>
      <Footer />
      <WhatsAppButton currentPage={currentPage} />
    </div>
  );
}

export default App;